import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Address from '../components/Address';
import css from '@styled-system/css';

import BusinessHours from '../components/BusinessHours';
import EmployeeTable from '../components/EmployeeTable';
import {
  FetchBranchResult,
  FetchAllBranchesResult,
  FetchEmployeesResult
} from '../types';
import { fetchAllBranches, fetchEmployeeByName } from '../api';
import { usePromiseFunction, useDebounce } from '../hooks';
import PageHeader from '../components/PageHeader';
import { SearchIcon } from '../components/Icons';
import { NoMatch } from '../components/NoMatch';
import { states } from '../configs/states';
import Link from '../components/Link';
import { H1, H2, H3, H4, H5, H6, Label, Spinner, Button } from '@wco/toolkit';
import { BankOrCreditUnionSchema } from '../components/MicroData';
import bankSEOConfig from '../configs/index';
import SEO from '../components/SEO';

const Img = styled.img`
  max-height: 11.25rem;
  max-width: 100%;
  height: auto;
  vertical-align: middle;
`;

const ImageHolder = styled.div`
  ${css({
    display: ['none', 'block'],
    paddingRight: ['0', 'xlarge'],
    paddingLeft: ['0', 'small'],
    float: ['none', 'left']
  })}

  width: 100%;
  margin-bottom: ${props => props.theme.space.large};
`;

const BranchItem = styled.div`
  border-bottom: 1px solid rgba(217, 217, 217, 0.8);

  &:last-child {
    border: 0 none;
  }
`;

const FlexThird = styled.div`
  ${css({
    display: ['block', 'flex', 'flex'],
    flexWrap: ['nowrap', 'wrap', 'nowrap']
  })}

  justify-content: space-between;
`;

const FlexThirdColumn = styled.div`
  ${css({
    marginTop: ['0', 'medium'],
    marginBottom: ['small', 'medium'],
    marginRight: ['0', 'medium'],
    marginLeft: ['0', 'medium'],
    width: ['100%', '33%']
  })}

  &:empty {
    display: none;
  }
`;

const FlexSecondColumn = styled.div`
  ${css({
    marginTop: ['0', 'medium'],
    marginBottom: ['small', 'medium'],
    marginRight: ['0', 'medium'],
    marginLeft: ['0', 'medium'],
    width: ['100%', '50%', '25%']
  })}

  &:empty {
    display: none;
  }
`;

const FlexFirstColumn = styled.div`
  ${css({
    marginTop: ['0', 'medium'],
    marginBottom: ['small', 'medium'],
    marginRight: ['0', 'medium'],
    marginLeft: ['0', 'medium'],
    width: ['100%', '100%', '41.66%']
  })}

  max-width: 100%;
  max-height: 11.25rem;
  height: auto;
  &:empty {
    display: none;
  }
`;

const AddressDetail = styled.div`
  ul {
    list-style: none;
    margin: 0;
    padding: 0;
    display: block;
  }

  li {
    overflow-wrap: break-word;
    word-wrap: break-word;
  }
`;

const OpeningDetail = styled.div`
  ${css({
    display: ['none', 'block', 'block']
  })}

  ul {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  li {
    overflow-wrap: break-word;
    word-wrap: break-word;
  }
`;

const BranchesContainer = styled.div`
  padding: 0 ${props => props.theme.space.xlarge};
`;

const SearchContainer = styled.div`
  ${css({
    width: ['100%', '25rem']
  })}
  padding-bottom: ${props => props.theme.space.medium};;
  padding-top: ${props => props.theme.space.large};;
`;

const InputContainer = styled.div`
  position: relative;
  display: block;
  background-color: #dbdbdb;

  input {
    -webkit-appearance: none;
    padding: ${props => [props.theme.space.small, props.theme.space.medium]};
    border: none;
    box-sizing: border-box;
    position: relative;
    display: block;
    margin: 0;
    background-color: #dbdbdb;
    color: ${props => props.theme.colors.palette.darkGrey};
    font-size: ${props => props.theme.fontSizes.text.medium};
    background: none;
    outline: none;
    height: 3.125rem;
    width: 100%;
    padding-right: ${props => props.theme.space.xxlarge};
    z-index: 1;
  }

  input[type='submit'] {
    display: none;
  }
`;

const OpeningHoursMobile = styled.span`
  ${css({
    display: ['flex', 'none']
  })}

  text-decoration: underline;
  cursor: pointer;
  margin-top: ${props => props.theme.space.small};
`;

type BranchOverviewProps = {
  branchOverviewHeadlineSize: string;
  searchHeaderText: string;
  nextRouter?: any;
};

export function BranchesOverview(props: BranchOverviewProps) {
  const { nextRouter } = props;
  const [query, setQuery] = useState('');
  const [employees, setEmployees] = useState<FetchEmployeesResult | null>({
    employees: [
      {
        employeeId: '',
        firstName: '',
        lastName: '',
        friendlyName: '',
        title: '',
        phone: '',
        mobileNo: '',
        url: '',
        email: '',
        showEmployeeOnWebsite: false,
        showImage: false,
        latestUpdate: '',
        descriptionContent: '',
        indexOfEmployees: 0,
        sortingOrder: 0
      }
    ]
  });
  const debounceSearchQuery = useDebounce(query, 500);

  const employeeResults = usePromiseFunction(
    fetchEmployeeByName,
    debounceSearchQuery
  );

  const branchesResults = usePromiseFunction(fetchAllBranches);
  const isSearching = query === '' ? false : true;

  useEffect(() => {
    if (employeeResults.state === states.ok) {
      setEmployees(employeeResults.data);
    }
  }, [employeeResults]);

  if (branchesResults.state === states.pending) {
    return <Spinner />;
  } else if (branchesResults.state === states.error) {
    return (
      <NoMatch
        header={'Der er sket en teknisk fejl'}
        message={'Vi arbejder på at løse problemet.'}
      />
    );
  } else if (branchesResults.state === states.ok) {
    return (
      <BranchesContainer>
        <SEO
          title={bankSEOConfig.branchOverviewPage.title}
          description={bankSEOConfig.branchOverviewPage.description}
        />
        <SearchContainer>
          <InputContainer>
            <SearchIcon />
            <input
              key="employee-search"
              placeholder="Søg efter medarbejdere"
              type="text"
              data-testid="search-form"
              onChange={e => {
                setQuery(e.target.value);
              }}
            />
          </InputContainer>
        </SearchContainer>
        {isSearching && employeeResults.state === states.ok ? (
          <>
            <PageHeader headerText={props.searchHeaderText} />
            {employees && <EmployeeSearchResults {...employees} />}
          </>
        ) : employeeResults.state === states.error ? (
          <PageHeader headerText={employeeResults.errorMessage} />
        ) : employeeResults.state === states.pending ? (
          <>
            <Spinner />
          </>
        ) : null}

        {!isSearching ? (
          <BranchItems
            nextRouter={nextRouter}
            branchOverviewHeadlineSize={props.branchOverviewHeadlineSize}
            branches={branchesResults.data.branches}
          />
        ) : null}
      </BranchesContainer>
    );
  } else {
    return <NoMatch header="Siden findes ikke" message="alt er væk" />;
  }
}

export type BrancItemsType = FetchAllBranchesResult & {
  nextRouter?: any;
};

export function BranchItems(props: BrancItemsType) {
  const { nextRouter } = props;
  const renderBranchName = (branch: FetchBranchResult) => {
    if (props.branchOverviewHeadlineSize === 'h1') {
      return <H1>{branch.branchName}</H1>;
    } else if (props.branchOverviewHeadlineSize === 'h2') {
      return <H2>{branch.branchName}</H2>;
    } else if (props.branchOverviewHeadlineSize === 'h3') {
      return <H3>{branch.branchName}</H3>;
    } else if (props.branchOverviewHeadlineSize === 'h4') {
      return <H4>{branch.branchName}</H4>;
    } else if (props.branchOverviewHeadlineSize === 'h5') {
      return <H5>{branch.branchName}</H5>;
    } else {
      return <H6>{branch.branchName}</H6>;
    }
  };

  const { branches } = props;
  const sortedListItems = branches.map((branch: FetchBranchResult) => (
    <BranchItem key={branch.id} data-testid={`${branch.branchName}-container`}>
      <div>
        <Label>Afdeling</Label>
        <Link to={`/${branch.friendlyName}`} nextRouter={nextRouter}>
          {renderBranchName(branch)}
        </Link>
      </div>
      <FlexThird>
        <FlexFirstColumn>
          {branch.url && (
            <Link to={`/${branch.friendlyName}`} nextRouter={nextRouter}>
              <ImageHolder>
                <Img
                  src={branch.url}
                  alt={`Billede af filialen ${branch.branchName}`}
                  loading="lazy"
                />
              </ImageHolder>
            </Link>
          )}
        </FlexFirstColumn>
        <FlexSecondColumn>
          <AddressDetail>
            <H5>Kontaktoplysninger</H5>
            <Address {...branch} />
            <OpeningHoursMobile>
              <Link to={`/${branch.friendlyName}`} nextRouter={nextRouter}>
                Se åbningstider
              </Link>
            </OpeningHoursMobile>
            <Link to={`/${branch.friendlyName}`} nextRouter={nextRouter}>
              <Button>Se medarbejdere</Button>
            </Link>
          </AddressDetail>
        </FlexSecondColumn>
        <FlexThirdColumn>
          <OpeningDetail>
            <H5>Åbningstider</H5>
            <BusinessHours opening={branch.openingHours}></BusinessHours>
          </OpeningDetail>
        </FlexThirdColumn>
      </FlexThird>
      <BankOrCreditUnionSchema {...branch} />
    </BranchItem>
  ));

  return <div>{sortedListItems}</div>;
}

export function EmployeeSearchResults(props: FetchEmployeesResult) {
  return (
    <div>
      <EmployeeTable employees={props.employees} />
    </div>
  );
}
